import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../axiosBaseQuery";
import {
    Assessment,
    AssessmentsPayload,
    AssessmentsResponse,
    UserResponses,
} from "@convin/type/lms";
import {
    CreateQuestionBankPayload,
    GetPayloadType,
    GoogleFormAssessment,
    UpdateFormResponseObject,
    UpdateQuestionBankPayload,
} from "@convin/modules/lms/assesments/context/types";
import {
    isDefined,
    updateDownloadQue,
} from "@convin/utils/helper/common.helper";
import {
    QuestionBankListPayload,
    QuestionBankItem,
    DocumentListPayload,
    DocumentType,
} from "@convin/modules/lms/assesments/question-bank/types";
import { Question } from "@convin/type/Audit";
import { PaginationType } from "@convin/type/Common";
import { DownloadReport } from "@convin/type/Report";

const config = {
    createForm: "coachings/forms/create/",
    updateForm: "coachings/forms/update/",
    assessementList: "coachings/assessments/",
};

export const lmsAssessmentApiSlice = createApi({
    reducerPath: "lmsAssessmentApiSlice",
    baseQuery: axiosBaseQuery({
        transformResponse: (response) => response,
    }),
    tagTypes: ["QuestionBank"],
    endpoints: (builder) => ({
        getAssessments: builder.query<AssessmentsResponse, AssessmentsPayload>({
            query: ({ page, ...payload }) => ({
                url: `${config.assessementList}`,
                method: "POST",
                keepUnusedDataFor: 30,
                body: { ...payload },
                params: { page },
            }),
        }),
        cloneAssessment: builder.mutation<
            Assessment,
            { id: number; title: string }
        >({
            query: ({ id, title }) => ({
                url: `${config.assessementList}clone/${id}/`,
                method: "POST",
                body: {
                    title: title,
                },
            }),
        }),
        deleteAssessment: builder.mutation<Assessment, number>({
            query: (payload) => ({
                url: `${config.assessementList}${payload}/`,
                method: "DELETE",
            }),
        }),
        getAssessmentGraphData: builder.query<
            UserResponses,
            {
                id: number;
                session_id?: number;
            }
        >({
            query: ({ id, session_id }) => ({
                url: `/coachings/assessment/${id}/graph/`,
                method: "GET",
                params: { session_id },
            }),
        }),
        createAssessment: builder.mutation<GoogleFormAssessment, void>({
            query: () => ({
                url: config.createForm,
                method: "POST",
            }),
        }),
        getAssessmentById: builder.query<GoogleFormAssessment, number>({
            query: (id) => ({
                url: `/${config.assessementList}${id}/`,
                method: "GET",
            }),
        }),
        getRepAssessmentById: builder.query<GoogleFormAssessment, number>({
            query: (id) => ({
                url: `/coachings/assessments/rep/${id}/`,
                method: "GET",
            }),
        }),
        batchUpdateQuestion: builder.mutation<
            UpdateFormResponseObject,
            GetPayloadType & { skipUpdate?: boolean }
        >({
            query: (payload) => ({
                url: `/coachings/forms/${payload.assessment_id}/update/`,
                method: "POST",
                body: { ...payload.data },
            }),
            async onQueryStarted(
                { assessment_id, skipUpdate },
                { dispatch, queryFulfilled }
            ) {
                try {
                    const {
                        data: { form },
                    } = await queryFulfilled;
                    if (isDefined(assessment_id) && !skipUpdate) {
                        dispatch(
                            lmsAssessmentApiSlice.util.updateQueryData(
                                "getAssessmentById",
                                assessment_id,
                                (draft) => {
                                    Object.assign(draft, {
                                        ...draft,
                                        title: form.info.title,
                                        form_data: form,
                                    });
                                }
                            )
                        );
                    }
                } catch {}
            },
        }),
        // reorderQuestion: builder.mutation<
        //     UpdateFormResponseObject,
        //     GetPayloadType
        // >({
        //     query: (payload) => ({
        //         url: `/coachings/forms/${payload.assessment_id}/update/`,
        //         method: "POST",
        //         body: { ...payload.data },
        //     }),
        //     async onQueryStarted(_, { queryFulfilled }) {

        //     },
        // }),
        downloadAssessmentResponses: builder.mutation<DownloadReport, number>({
            query: (id) => ({
                url: `/coachings/assessment/download/${id}/`,
                method: "GET",
                responseType: "blob",
            }),
            async onQueryStarted(_, { queryFulfilled, dispatch }) {
                try {
                    const { data } = await queryFulfilled;
                    updateDownloadQue({
                        data,
                        dispatch,
                        message:
                            "Response sheet is being downloaded. You will be notified once it is ready.",
                    });
                } catch (err) {}
            },
        }),
        //Question Bank
        getParameterByTemplateId: builder.query<Question[], number>({
            query: (id) => `/audit/question/list_by_template/${id}/`,
        }),
        getQuestionBank: builder.query<
            PaginationType<QuestionBankItem>,
            QuestionBankListPayload & { next?: string }
        >({
            query: ({ next, ...payload }) => ({
                url: next || "/library/questionbank/",
                method: "POST",
                body: { ...payload },
            }),
            providesTags: ["QuestionBank"],
        }),
        getDocumentList: builder.query<
            PaginationType<DocumentType>,
            DocumentListPayload & { next?: string }
        >({
            query: ({ next, ...payload }) => ({
                url: next || "/library/meeting/media/",
                method: "POST",
                body: { ...payload },
            }),
        }),
        deleteQuestion: builder.mutation<Assessment, string>({
            query: (payload) => ({
                url: `/library/questionbank/delete/${payload}/`,
                method: "DELETE",
            }),
        }),

        createQuestionBank: builder.mutation<
            { questionItems: QuestionBankItem[] },
            CreateQuestionBankPayload
        >({
            query: (payload) => ({
                url: "/library/questionbank/create/",
                method: "POST",
                body: { ...payload },
            }),
        }),
        updateQuestionBank: builder.mutation<
            QuestionBankItem,
            UpdateQuestionBankPayload
        >({
            query: (payload) => ({
                url: "/library/questionbank/update/",
                method: "POST",
                body: { ...payload },
            }),
        }),
        generateQuestionBank: builder.mutation<
            { questions: QuestionBankItem[]; media: string },
            {
                difficulty: string;
                question_count: number;
                file: string;
                document_text: string;
            }
        >({
            query: (payload) => ({
                url: "/library/questionbank/generate_questions/",
                method: "POST",
                body: payload,
            }),
            invalidatesTags: ["QuestionBank"],
        }),
        getParameters: builder.query<Question[], void>({
            query: () => "/audit/question/list_all/",
        }),
        getMediaFiles: builder.query<
            { mediaFiles: { fileName: string; media: string }[] },
            void
        >({
            query: () => "library/questionbank/media_files/",
        }),
    }),
});

export const {
    useGetAssessmentsQuery,
    useLazyGetAssessmentsQuery,
    useCreateAssessmentMutation,
    useGetAssessmentByIdQuery,
    useLazyGetAssessmentByIdQuery,
    useBatchUpdateQuestionMutation,
    useGetAssessmentGraphDataQuery,
    useDeleteAssessmentMutation,
    useDownloadAssessmentResponsesMutation,
    useGetRepAssessmentByIdQuery,
    useLazyGetRepAssessmentByIdQuery,
    useCloneAssessmentMutation,
    useLazyGetQuestionBankQuery,
    useGetQuestionBankQuery,
    useDeleteQuestionMutation,
    useCreateQuestionBankMutation,
    useUpdateQuestionBankMutation,
    useGetParameterByTemplateIdQuery,
    useGetParametersQuery,
    useGetDocumentListQuery,
    useLazyGetDocumentListQuery,
    useGenerateQuestionBankMutation,
    useGetMediaFilesQuery,
} = lmsAssessmentApiSlice;
